import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AccessCardFindInterface } from '@zaoblako/shared/modules/pacs/dtos/access-card/access-card-find.dto';
import {
  AccessCardFormDto,
  AccessCardFormInterface
} from '@zaoblako/shared/modules/pacs/dtos/access-card/access-card-form.dto';
import { AccessCardTypeEnum } from '@zaoblako/shared/modules/pacs/enums/access-card-type.enum';
import { RoomAccessFindInterface } from '@zaoblako/shared/modules/security/dtos/room-access/room-access-find.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { AccessCardService } from '@zaoblako/web/core/services/control/access-card.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { FiltersService } from '@zaoblako/web/core/services/filters.service';
import { ExternalUserStoreService } from '@zaoblako/web/modules/security/components/external-user/card/services/external-user-store.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DaterangepickerComponent } from '@zaoblako/web/shared/dumbs/daterangepicker/daterangepicker.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { AccessCardTypeNamePipe } from '@zaoblako/web/shared/pipes/access-card-type-name.pipe';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Observable } from 'rxjs';

@Component({
  selector: 'crm-access-card-form',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    SpinnerComponent,
    ButtonModule,
    DaterangepickerComponent,
    InputNumberModule,
    FormsModule,
    CheckboxModule,
    SelectButtonModule
  ],
  templateUrl: './access-card-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccessCardFormComponent extends BaseFormComponent<AccessCardFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public card: AccessCardFormInterface = <AccessCardFormInterface>{};

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;
  public settings$: Observable<RoomAccessFindInterface>;

  public dto = AccessCardFormDto;
  public engine: 'class-transformer' | 'serde' = 'serde';

  public readonly accessCardTypeOptions: { label: string; value: AccessCardTypeEnum }[] = Object.values(
    AccessCardTypeEnum
  )
    .filter((value) => value !== AccessCardTypeEnum.TEST_MOCK_CARD)
    .map((value) => ({
      label: new AccessCardTypeNamePipe().transform(value),
      value: value
    }));

  public constructor(
    private readonly accessCardService: AccessCardService,
    private readonly formStoreService: FormStoreService,
    private readonly confirmService: ConfirmDialogService,
    private readonly errorService: ErrorService,
    private readonly filtersService: FiltersService,
    private readonly externalUserStoreService: ExternalUserStoreService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);
    this.settings$ = this.externalUserStoreService.select(({ accessControlInfo }) => accessControlInfo.settings);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    } else {
      this.card.user = this.formStoreService.getState.forms[this.index].data?.user;
      this.card.externalUser = this.formStoreService.getState.forms[this.index].data?.externalUser;
      this.card.isActive = !this.externalUserStoreService.getState.accessControlInfo.settings?.blocked;
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.accessCardService
      .findById<AccessCardFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (card) => {
          this.card = card;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveCard() {
    if (this.checkValidate(this.card).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.accessCardService.update<AccessCardFormInterface, AccessCardFindInterface>(this.card).subscribe({
          next: (card) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Карта "${card.code}" обновлена`);
            this.formStoreService.saveNext(card, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.accessCardService.create<AccessCardFindInterface>(this.card).subscribe({
          next: (card) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Карта создана');
            this.formStoreService.saveNext(card, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteCard() {
    this.confirmService.confirm({
      message: `Вы действительно желаете удалить карту "${this.card?.code}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.accessCardService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe(() => {
          this.errorService.addSuccessMessage(`Карта "${this.card?.code}" удалена`);
          this.formStoreService.deleteNext(this.index);
        });
      }
    });
  }

  public setDate(dates: [Date, Date]) {
    if (!dates) {
      this.card.begin = null;
      this.card.end = null;
      return;
    }
    [this.card.begin, this.card.end] = this.filtersService.formatISOPeriod(dates);
  }
}
